// -------------- Hook to call the api endpoint for the batch stocks ----------------
// ----------------------------------------------------------------------------------
import { useState, useEffect, useRef } from 'react';
import { getBatchStockData } from '../services/api';

// function to fetch batch stock data for a list of 20 tickers at a time and update the list
export const useBatchStockData = (tickers, timeframe) => {

  // create state variables for watchlistData and loading
  const [watchlistData, setWatchlistData] = useState([]);
  const [loading, setLoading] = useState(false);
  const timeframeRef = useRef(timeframe);

  useEffect(() => {
    timeframeRef.current = timeframe; // update the current timeframe when it changes
  }, [timeframe]);

  useEffect(() => {
    if (!tickers || tickers.length === 0) return; // if there are no tickers, do not fetch anything

    let cancelled = false; // flag used to prevent state updates if unmounted 
    setLoading(true); // start loading when the request is initiated

    // function to fetch all stock data in batches of 20
    const fetchAll = async () => {

      try {

        // loop through all the tickers in the batch
        for (let idx = 0; idx < tickers.length; idx += 20) { 
          if (cancelled) return; // if the component is unmounted, stop fetching

          const batch = tickers.slice(idx, idx + 20); // create the batch of 20
          const symbolString = batch.map((t) => t.symbol).join(','); // create a comma-separated string of symbols for the API request
          
          try { // try to fetch the batch data

            // call the api to get the data
            const response = await getBatchStockData({ symbols: symbolString, timeframe });

            // if the component is unmounted or time changes stop updating the state
            if (cancelled || timeframe !== timeframeRef.current) return; 

            setWatchlistData((prev) => { // update the watchlistData state variable with the new data
              // convert the prev data into an object with the symbol as key for easy updating
              const updated = { ...Object.fromEntries(prev.map((w) => [w.symbol, w])) };

              batch.forEach((ticker) => { // loop through each ticker in the batch and update the data
                const stockData = response.stocks?.[ticker.symbol]; // get the stock data for the ticker from the response
                
                updated[ticker.symbol] = { // update the stock data for the ticker
                  symbol: ticker.symbol,
                  name: ticker.name,
                  price: stockData?.price || 0,
                  change: stockData?.changePercent || 0,
                };
                
              });
              return Object.values(updated); // convert the updated object back into an array and return it to update the state

            });
          } catch (batchErr) { // if there is an error fetching the batch, log it and continue with the next batch
            console.error(`Error fetching batch ${idx}–${idx + 20}:`, batchErr);
          }
        }

      } finally {
        if (!cancelled) setLoading(false); // set loading to false when all requests are complete
      }
    };

    fetchAll(); // run the fetchAll function to start fetching the data
    return () => { cancelled = true; }; 
  }, [tickers, timeframe]);

  return { watchlistData, loading }; // return the data and the state
};
